import type { RGB, WizBulb } from '../types/ambilight';
import { rgbToRgbw } from './colorEngine';

interface BulbLastState {
  r: number;
  g: number;
  b: number;
  dimming: number;
  c?: number;
  w?: number;
  state?: boolean;
}

/**
 * Cliente WiZ: Conexión híbrida con relé UDP a través de Vite dev server.
 * Las peticiones al endpoint local '/api/wiz/pilot' son emitidas de forma
 * ultra-rápida y directa vía UDP al foco físico (192.168.100.127:38899).
 */
export class WizClient {
  private lastSentTime = 0;
  private minIntervalMs = 50; // 20 FPS máximo para no saturar el buffer UDP del foco
  private lastSentByIp: Map<string, BulbLastState> = new Map();
  private isServerOnline = true;

  constructor() {
    this.checkServerHealth();
  }

  public async checkServerHealth(): Promise<boolean> {
    try {
      const res = await fetch('/api/wiz/pilot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ping: true }),
      });
      this.isServerOnline = res.ok;
      return res.ok;
    } catch {
      this.isServerOnline = false;
      return false;
    }
  }

  public getIsServerOnline(): boolean {
    return this.isServerOnline;
  }

  public async connectBulb(ip: string): Promise<{ ok: boolean; reachable?: boolean }> {
    try {
      const res = await fetch('/api/wiz/pilot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip, ping: true }),
      });
      return { ok: res.ok, reachable: res.ok };
    } catch {
      return { ok: true, reachable: true };
    }
  }

  public async sendColorFrame(
    bulb: WizBulb,
    color: RGB,
    dimming: number,
    white: number,
    rawBrightness: number
  ): Promise<void> {
    const now = performance.now();
    const prev = this.lastSentByIp.get(bulb.ip);
    const shouldTurnOff = rawBrightness < 2 || dimming < 5;

    // El apagado SIEMPRE tiene prioridad sobre el rate limiter:
    // Si la pantalla se pone negra no esperamos 50ms para apagar el foco.
    if (!shouldTurnOff && now - this.lastSentTime < this.minIntervalMs) {
      return; // Rate limiting por hardware (solo para frames activos)
    }

    // Escena oscura -> apagado para no emitir luz parásita
    if (shouldTurnOff) {
      if (prev && prev.state === false) {
        return;
      }
      this.lastSentTime = now;
      this.lastSentByIp.set(bulb.ip, { r: 0, g: 0, b: 0, dimming: 0, state: false });

      fetch('/api/wiz/pilot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip: bulb.ip, state: false }),
      }).catch(() => {});
      return;
    }

    // dim: si dimming llega a 0 ya fue filtrado arriba por shouldTurnOff.
    // Solo aplicamos el floor de 10 si el foco está ACTIVO (dimming > 0).
    const dim = Math.max(10, Math.min(100, Math.round(dimming)));

    // Síntesis de 5 canales (R, G, B, C, W) de máxima fidelidad fotométrica:
    // - c (Cold White 6500K): blanco puro monitor D65
    // - w (Warm White 2700K): luz cálida / dorada / atardecer / fuego
    // - r, g, b: croma puro descontando la emisión de los blancos
    const decomp = rgbToRgbw(color.r, color.g, color.b);
    const r = decomp.r;
    const g = decomp.g;
    const b = decomp.b;
    const c = decomp.c;
    const w = decomp.w;

    // Filtro de delta insignificante: evita inundar la red wifi con paquetes idénticos
    if (
      prev &&
      prev.state !== false &&
      Math.abs(prev.r - r) < 3 &&
      Math.abs(prev.g - g) < 3 &&
      Math.abs(prev.b - b) < 3 &&
      Math.abs((prev.c ?? 0) - c) < 3 &&
      Math.abs((prev.w ?? 0) - w) < 3 &&
      Math.abs(prev.dimming - dim) < 3
    ) {
      return;
    }

    this.lastSentTime = now;
    this.lastSentByIp.set(bulb.ip, { r, g, b, dimming: dim, c, w, state: true });

    fetch('/api/wiz/pilot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ip: bulb.ip,
        state: true,
        r,
        g,
        b,
        dimming: dim,
        c,
        w,
      }),
    }).catch(() => {});
  }
}

export const wizClient = new WizClient();
